import type {Config as GRPCConfig} from '@aboutyou/grpc-clients/tadarida/aysa_api/shared/v1/config.types';
import {useMutation} from '../react-query';
import type {InferResponseType, MakeAllKeysRequired} from '../types';
import {
  useGetConsideredAbTestsFromGrpcTrailers,
  useGrpcClientConfig,
  useSetAbTestsFromTrailers,
} from './GrpcClientContext';
import {isUnimplementedError} from './code';
import {convertAllNullPropertiesToUndefined} from './convertAllNullPropertiesToUndefined';
import {
  GenericGrpcRequestCallbackOptions,
  GrpcMutationConfig,
  GrpcRequestServiceMethod,
  useGenericGrpcRequestFn,
} from './useGenericGrpcRequest';

type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>;
};

export interface GenericTadaridaConfig {
  config?: DeepPartial<GRPCConfig>;
}

export function useGrpcRequestMutation<
  ServiceMethod extends GrpcRequestServiceMethod,
  ParamsType extends MakeAllKeysRequired<
    Omit<
      Parameters<Awaited<ReturnType<ServiceMethod>>>[1],
      keyof GenericTadaridaConfig
    >
  > &
    GenericGrpcRequestCallbackOptions,
  ResponseType extends InferResponseType<
    ReturnType<Awaited<ReturnType<ServiceMethod>>>
  > = InferResponseType<ReturnType<Awaited<ReturnType<ServiceMethod>>>>,
>(
  lazyAsyncService: ServiceMethod,
  config: GrpcMutationConfig<ResponseType> = {},
) {
  const genericConfig = useGrpcClientConfig();
  const {mockData, resolveMockData = 'not-implemented'} = config;

  const fetch = useGenericGrpcRequestFn(
    lazyAsyncService,
    {} as ParamsType,
    {
      config: genericConfig,
    } as GenericTadaridaConfig,
  );

  const originalFetch = (params: ParamsType) =>
    fetch(convertAllNullPropertiesToUndefined<ParamsType>(params));

  let fetcher = originalFetch;

  if (resolveMockData !== 'never' && mockData) {
    fetcher = (params: ParamsType) => {
      return new Promise<ResponseType>((resolve, reject) => {
        if (resolveMockData === 'always') {
          mockData(params).then(resolve);
        } else {
          originalFetch(params)
            .then(resolve)
            .catch(async error => {
              if (
                isUnimplementedError(error) &&
                resolveMockData === 'not-implemented'
              ) {
                return mockData(params).then(resolve);
              }
              return reject(error);
            });
        }
      });
    };
  }

  const addAbTestsFromTrailers = useSetAbTestsFromTrailers();
  const getConsideredAbTestsFromGrpcTrailers =
    useGetConsideredAbTestsFromGrpcTrailers();

  return useMutation<ResponseType, unknown, ParamsType, unknown>(fetcher, {
    ...config,
    onSuccess: result => {
      const consideredAbTestsFromGrpcTrailers =
        getConsideredAbTestsFromGrpcTrailers(result?.trailers);

      if (consideredAbTestsFromGrpcTrailers.length) {
        addAbTestsFromTrailers(consideredAbTestsFromGrpcTrailers);
      }

      config.onSuccess?.(result);
    },
  });
}
